import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Mail,
  Loader2,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Send,
  Construction,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// Generic "Coming Soon" modal — used for Careers, Sign In, etc.
// Optionally captures email for "notify me" via /api/newsletter
// ============================================================
export function ComingSoonModal({ open, onClose, title, blurb, captureEmail = true, dataPrefix = "coming-soon" }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setEmail("");
        setBusy(false);
        setDone(false);
        setErr("");
      }, 250);
    }
  }, [open]);

  const subscribe = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setDone(true);
    } catch (e2) {
      setErr("Could not save your email. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="cs-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          data-testid={`${dataPrefix}-modal`}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center px-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#0d0d14] border border-white/10 p-8"
          >
            <button
              onClick={onClose}
              data-testid={`${dataPrefix}-close`}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-black/50 hover:bg-[#FF2D9C] hover:text-white text-white/70 border border-white/10"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FF2D9C]/10 border border-[#FF2D9C]/40 rounded-full mb-4">
              <Construction className="w-6 h-6 text-[#FF2D9C]" />
            </div>
            <div className="font-accent text-xs text-[#00E5FF] tracking-[0.22em] mb-2">
              COMING SOON
            </div>
            <h3 className="font-heading text-2xl font-black mb-3">{title}</h3>
            <p className="text-white/70 text-sm leading-relaxed">
              {blurb}
            </p>

            {captureEmail && !done && (
              <form onSubmit={subscribe} className="mt-6 space-y-3">
                <label className="block font-accent text-[10px] tracking-[0.22em] text-white/55 mb-1">
                  GET NOTIFIED
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input
                      type="email"
                      placeholder="you@domain.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid={`${dataPrefix}-email`}
                      className="w-full bg-black/40 border border-white/10 focus:border-[#00E5FF] pl-9 pr-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid={`${dataPrefix}-submit`}
                    className="bg-[#FF2D9C] text-white font-black px-4 uppercase tracking-[0.22em] text-xs glow-pink transition-all disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Notify Me"}
                  </button>
                </div>
                {err && (
                  <div className="flex items-center gap-2 text-xs text-[#FF2D9C]">
                    <AlertCircle className="w-3.5 h-3.5" /> {err}
                  </div>
                )}
              </form>
            )}

            {done && (
              <div
                data-testid={`${dataPrefix}-success`}
                className="mt-6 flex items-center gap-2 text-sm text-emerald-400 border border-emerald-400/40 bg-emerald-400/5 px-3 py-2.5"
              >
                <CheckCircle2 className="w-4 h-4" />
                We&rsquo;ll let you know the moment this is live.
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


// ============================================================
// Submit Your Story modal — real form, posts to /api/stories
// ============================================================
const STORY_CATEGORIES = ["trucking", "biking", "cars", "boating", "other"];

export function SubmitStoryModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    title: "",
    story: "",
    category: "other",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setForm({ name: "", email: "", title: "", story: "", category: "other" });
        setBusy(false);
        setErr("");
        setDone(false);
      }, 250);
    }
  }, [open]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.name.trim().length < 2) return setErr("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return setErr("Please enter a valid email.");
    if (form.title.trim().length < 2) return setErr("Give your story a title.");
    if (form.story.trim().length < 20)
      return setErr("Tell us a bit more (min 20 characters).");
    setBusy(true);
    try {
      await axios.post(`${API}/stories`, {
        name: form.name,
        email: form.email,
        title: form.title,
        story: form.story,
        category: form.category,
      });
      setDone(true);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="story-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          data-testid="story-modal"
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-6"
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl bg-[#0d0d14] border border-white/10 min-h-screen sm:min-h-0"
          >
            <button
              onClick={onClose}
              data-testid="story-close"
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-black/50 hover:bg-[#FF2D9C] hover:text-white text-white/70 border border-white/10"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="px-6 sm:px-10 pt-8 pb-5 border-b border-white/10">
              <div className="font-accent text-xs text-[#FF2D9C] tracking-[0.22em] mb-1">
                AUTOMOTOVILLE · STORY TIPS
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-black">
                {done ? "Story received." : "Submit Your Story"}
              </h2>
            </div>

            {done ? (
              <div className="px-6 sm:px-10 py-10 text-center" data-testid="story-success">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-400/10 border border-emerald-400/40 mb-5">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="font-heading text-2xl font-black mb-3">
                  Thanks — we&rsquo;ve got it.
                </h3>
                <p className="text-white/70 max-w-md mx-auto">
                  A producer will read it. If it sparks something, we&rsquo;ll
                  reach out at <span className="text-[#00E5FF]">{form.email}</span>.
                </p>
                <button
                  onClick={onClose}
                  data-testid="story-success-close"
                  className="mt-6 bg-[#FF2D9C] text-white font-black px-8 py-3 uppercase tracking-[0.22em] text-sm glow-pink"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-6 sm:px-10 py-7 space-y-5">
                <p className="text-sm text-white/60 leading-relaxed">
                  Got a wild rig, a road-life moment, or someone the world should
                  meet? Tell us in your own words — no pitch deck required.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="YOUR NAME *">
                    <input
                      data-testid="story-name"
                      className={fieldCls}
                      value={form.name}
                      onChange={(e) => update({ name: e.target.value })}
                      maxLength={120}
                    />
                  </Field>
                  <Field label="EMAIL *">
                    <input
                      data-testid="story-email"
                      type="email"
                      className={fieldCls}
                      value={form.email}
                      onChange={(e) => update({ email: e.target.value })}
                    />
                  </Field>
                </div>

                <Field label="STORY TITLE *">
                  <input
                    data-testid="story-title"
                    className={fieldCls}
                    value={form.title}
                    onChange={(e) => update({ title: e.target.value })}
                    maxLength={140}
                    placeholder="e.g., The 1972 K10 my granddad built me"
                  />
                </Field>

                <Field label="CATEGORY">
                  <select
                    data-testid="story-category"
                    className={fieldCls}
                    value={form.category}
                    onChange={(e) => update({ category: e.target.value })}
                  >
                    {STORY_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label={`THE STORY * (${form.story.length}/4000)`}>
                  <textarea
                    data-testid="story-body"
                    className={fieldCls + " resize-none min-h-[160px]"}
                    rows={7}
                    maxLength={4000}
                    value={form.story}
                    onChange={(e) => update({ story: e.target.value })}
                    placeholder="Tell it the way you'd tell it at a campfire."
                  />
                </Field>

                {err && (
                  <div
                    data-testid="story-error"
                    className="flex items-start gap-2 text-sm text-[#FF2D9C] border border-[#FF2D9C]/40 bg-[#FF2D9C]/10 px-3 py-2"
                  >
                    <AlertCircle className="w-4 h-4 mt-0.5" /> {err}
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="font-accent text-xs tracking-[0.22em] text-white/60 hover:text-white"
                  >
                    CANCEL
                  </button>
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="story-submit"
                    className="flex items-center gap-2 bg-[#FF2D9C] text-white font-black px-7 py-3 uppercase tracking-[0.22em] text-xs glow-pink hover:scale-[1.02] transition-all disabled:opacity-60"
                  >
                    {busy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    Submit Story
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


// ============================================================
// Contact form — inline section component
// ============================================================
export function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.name.trim().length < 1) return setErr("Name is required.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return setErr("Please enter a valid email.");
    if (form.message.trim().length < 5) return setErr("Message is too short.");
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
    } catch (e2) {
      setErr("Could not send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="contact-success"
        className="border border-emerald-400/40 bg-emerald-400/5 p-6 text-center"
      >
        <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
        <h4 className="font-heading text-2xl font-black">Message sent.</h4>
        <p className="text-white/70 mt-2">A producer will get back to you soon.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className="space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="NAME *">
          <input
            data-testid="contact-name"
            className={fieldCls}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            maxLength={120}
          />
        </Field>
        <Field label="EMAIL *">
          <input
            data-testid="contact-email"
            type="email"
            className={fieldCls}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
      </div>
      <Field label="MESSAGE *">
        <textarea
          data-testid="contact-message"
          className={fieldCls + " resize-none min-h-[140px]"}
          rows={6}
          value={form.message}
          maxLength={2000}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
        />
      </Field>
      {err && (
        <div
          data-testid="contact-error"
          className="flex items-start gap-2 text-sm text-[#FF2D9C] border border-[#FF2D9C]/40 bg-[#FF2D9C]/10 px-3 py-2"
        >
          <AlertCircle className="w-4 h-4 mt-0.5" /> {err}
        </div>
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy}
          data-testid="contact-submit"
          className="flex items-center gap-2 bg-[#FF2D9C] text-white font-black px-6 py-3 uppercase tracking-[0.22em] text-xs glow-pink disabled:opacity-60 transition-all hover:scale-[1.02]"
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5" />
          )}
          Send
        </button>
      </div>
    </form>
  );
}


// ============================================================
// Newsletter inline form — section component
// ============================================================
export function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setDone(true);
      setEmail("");
    } catch (e2) {
      setErr("Could not subscribe. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="newsletter-form"
      className="flex flex-col sm:flex-row gap-3 max-w-xl"
    >
      <div className="relative flex-1">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <input
          type="email"
          placeholder="you@domain.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="newsletter-email"
          className="w-full bg-black/40 border border-white/10 focus:border-[#00E5FF] pl-10 pr-4 py-3 text-white placeholder-white/30 outline-none transition-colors"
        />
      </div>
      <button
        type="submit"
        disabled={busy || done}
        data-testid="newsletter-submit"
        className="bg-[#FF2D9C] text-white font-black px-6 py-3 uppercase tracking-[0.22em] text-sm glow-pink disabled:opacity-60 transition-all hover:scale-[1.02] flex items-center gap-2 justify-center"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Sparkles className="w-4 h-4" />
        )}
        {done ? "Subscribed" : "Subscribe"}
      </button>
      {err && (
        <div
          data-testid="newsletter-error"
          className="basis-full text-xs text-[#FF2D9C] flex items-center gap-1"
        >
          <AlertCircle className="w-3.5 h-3.5" /> {err}
        </div>
      )}
      {done && (
        <div
          data-testid="newsletter-success"
          className="basis-full text-xs text-emerald-400 flex items-center gap-1"
        >
          <CheckCircle2 className="w-3.5 h-3.5" /> You&rsquo;re on the list.
        </div>
      )}
    </form>
  );
}

// ---------- shared ----------
const fieldCls =
  "w-full bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors";

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-accent text-[10px] tracking-[0.22em] text-white/55 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}
